import { Trans, useLingui } from '@lingui/react/macro';

import Box from '@/design_system/Box';
import FoldableSection from '@/design_system/FoldableSection';
import Stack from '@/design_system/Stack';
import Title from '@/design_system/Typography/Title';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import {
  ConditionQuestion,
  ConditionQuestionError,
} from '@/routes/Requests/New/components/ConditionForm/ConditionQuestion';
import { QualityControllerSelect } from '@/routes/Requests/New/components/ConditionForm/QualityControllerSelect';
import { WeightInput } from '@/routes/Requests/New/components/ConditionForm/WeightInput';

import { ActionQuestions } from './ActionQuestions';
import { ConditionContextAndPhotos } from './ConditionContextAndPhotos';
import { ConditionQuestionReadonlyAnswers } from './ConditionQuestionReadonlyAnswers';
import { RepairedPhotoUpload } from './RepairedPhotoUpload';

export const QualityControlForm = () => {
  const { t } = useLingui();
  const { article, qualityControlFormData, view, errors } = useArticleContext();

  if (!view.qualityControl.shown || !qualityControlFormData) {
    return null;
  }

  const { conditionForm, articleConditionForms } = qualityControlFormData;

  const groupArticleConditionForms = articleConditionForms
    .filter(
      (articleConditionForm) =>
        articleConditionForm && !articleConditionForm.previousArticleConditionForms?.length
    )
    .map((articleConditionForm) =>
      [
        articleConditionForm,
        articleConditionForms.find(({ previousArticleConditionForms }) =>
          previousArticleConditionForms.find(({ id }) => id === articleConditionForm.id)
        ),
      ].filter((form) => !!form)
    );

  return groupArticleConditionForms.map(
    (groupArticleConditionForm, groupArticleConditionFormIndex) => {
      const isEditable = !!groupArticleConditionForm.find((form) => !form.completedAt);

      return (
        <FoldableSection
          key={`${groupArticleConditionForm[0].id}-${article?.step?.step}-${article?.taskType}`}
          label={
            <Title as="h2">
              <Trans id="requests.articles.quality-control.title">Quality control</Trans>
              {groupArticleConditionForms.length > 1
                ? ` #${groupArticleConditionForms.length - groupArticleConditionFormIndex}`
                : null}
            </Title>
          }
          startClosed={!isEditable || article.step?.step !== 'quality-control'}
          isInvalid={isEditable && errors.qualityControl?.hasError}
        >
          <Box padding="1rem" gap="1rem" className="bg-neutral-0">
            <Stack
              gap="2rem"
              ariaLabel={
                isEditable
                  ? t({
                      id: 'requests.articles.quality-control.editable',
                      message: 'Editable quality control form',
                    })
                  : undefined
              }
            >
              <QualityControllerSelect articleConditionForms={groupArticleConditionForm} />
              <WeightInput articleConditionForms={groupArticleConditionForm} />
              <RepairedPhotoUpload articleConditionForms={groupArticleConditionForm} />
              <Stack gap="0.5rem">
                <h3 className="label-100-medium">
                  <Trans id="requests.articles.drawer.quality-control.conditions.title">
                    Item condition
                  </Trans>
                </h3>
                <Stack gap="1rem">
                  {conditionForm.questions.map((question) => (
                    <Stack key={question.id} gap="0.25rem">
                      <Box padding="0rem">
                        <ConditionQuestion
                          question={question}
                          type="quality-control"
                          articleConditionForms={groupArticleConditionForm}
                        />
                        <ConditionQuestionReadonlyAnswers
                          question={question}
                          articleConditionForms={groupArticleConditionForm}
                        />
                        <ConditionContextAndPhotos
                          question={question}
                          type="quality-control"
                          articleConditionForms={groupArticleConditionForm}
                        />
                      </Box>
                      {isEditable && (
                        <ConditionQuestionError question={question} type="quality-control" />
                      )}
                    </Stack>
                  ))}
                </Stack>
              </Stack>
              <ActionQuestions articleConditionForms={groupArticleConditionForm} />
            </Stack>
          </Box>
        </FoldableSection>
      );
    }
  );
};
